/**
 * Business Units — a PLAIN LIST of records on the module's record-list pattern.
 *
 * THREE OWNER RULINGS SHAPE THIS SCREEN, all 2026-10-05:
 *
 *   D-018 *"use that [the Knowledge Library screen] as the starting point to design screens in ERP
 *   / Functional Area Consolidation module. Apply that across all screen when multiple records
 *   should be presented on single screen. Used the same icon pools."* — so the filter bar, the
 *   table, the badges, the row-action glyph buttons and the empty / loading / error states are
 *   `../chrome/recordList.css`, which clones `pages/apr/settings/KnowledgeLibrary.tsx`. Cloned,
 *   never imported: that is another product's folder (D-003).
 *
 *   D-017 *"replace all windows from this app by 'right panel'"* — so Add and Edit open
 *   `BusinessUnitDetailPanel`, a right-side panel, and never a centred dialog.
 *
 *   D-022 *"There should be no Parent for Business Unit. Your design went way too dep - a Business
 *   Unit (BU) is a BU"* and *"I do not need Code not Order … Also remove Parent and Active flag.
 *   All Business Units will be only active if they are defined"* — **so the tree is gone.** No
 *   indentation, no `▸`/`▾` toggles, no Code column, no Order column, no Children column, no status
 *   filter and no parent picker in the panel. A business unit is name, description, region and
 *   country, and the screen shows exactly those four with the assessment-unit count beside them.
 *
 * WHAT THE TREE COST, SO IT IS NOT REBUILT BY SOMEBODY READING THE OLD CODE. `visibleRows` walked
 * a parent map, repeated the server's three-key sort because `sort_order` had no uniqueness
 * constraint, appended orphans whose parent was missing from the payload, and carried a rule that
 * a filter match drags its ancestors in with it. All four existed only to make a hierarchy render
 * correctly. **None of them is needed by a list, and a list is what the owner asked for.** What
 * survives is the filter: one search box over the fields the table shows.
 *
 * WHICH HALF OF THE MODULE THIS SCREEN IS IN. It renders inside `ErpScreen`/`.erp-app` with
 * `chrome/PageHeader`, which is the module's cloned-CSS vocabulary (it was once the module's one
 * MUI screen; D-018 moved it). `ConfirmDialog` is still imported from `@platform/ui-kit` and always
 * will be — SCREEN_MAP is explicit ("Do not clone the ui-kit"), and the ui-kit is the one layer
 * both halves share.
 *
 * THE HEADER NEVER SCROLLS. `useScreenLock` measures the height; the table wrapper is the one
 * scroller and its column headers are sticky. Unchanged by D-022, and stated here because it is
 * the thing most easily lost when a screen is edited (CLAUDE.md, "A screen header never scrolls").
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { erpApi } from '../api';
import { type BusinessUnit, type ErpContext } from '../types';
import { canAdministerInitiatives } from '../roles';
import { BusinessUnitDetailPanel } from './BusinessUnitDetailPanel';
import '../chrome/recordList.css';
import './BusinessUnitHierarchy.css';

const BusinessUnitsHelp: Help = {
  title: 'Business Units',
  icon: '🏢',
  purpose:
    'The organisation this module assesses across — the business units that own systems. It is <strong>install-wide, not per initiative</strong>: an organisation chart outlives any one consolidation exercise.',
  doHere: [
    'Click <strong>ADD BUSINESS UNIT</strong> to add one. A unit is its <strong>name</strong>, an optional <strong>description</strong>, and the <strong>region</strong> and <strong>country</strong> it sits in.',
    'Edit a unit to rename it, change its description, or change its region or country.',
    'Use the search box to narrow the list by name, description, region or country.',
    'A unit cannot be deleted while any initiative is still assessing it. The server says so, and that message is shown exactly as it comes back.',
  ],
  fits: 'ERP Consolidation &rarr; Business Units',
  firstTime:
    'Add the units you will actually assess — the divisions, plants or regions that own systems. Use Region and Country to group them.',
  sections: [
    {
      type: 'html',
      label: 'A business unit is flat',
      html: 'There is <strong>no parent</strong> and no hierarchy: a BU is a BU. Use <strong>Region</strong> and <strong>Country</strong> to group the list — they are facts about the unit rather than a structure somebody has to maintain.',
    },
    {
      type: 'html',
      label: 'Every unit is active',
      html: 'There is no active flag. A unit is active because it is defined; one that should no longer be used is <strong>deleted</strong>.',
    },
  ],
};

/**
 * Which units survive the search.
 *
 * Every field the table SHOWS is searchable, and nothing it does not: a search that matched a
 * hidden column would look like it had matched nothing.
 */
function filterUnits(units: readonly BusinessUnit[], search: string): BusinessUnit[] {
  const needle = search.trim().toLowerCase();
  if (needle === '') return [...units];
  return units.filter((unit) =>
    [unit.name, unit.region, unit.country, unit.description].some(
      (value) => typeof value === 'string' && value.toLowerCase().includes(needle),
    ),
  );
}

export function BusinessUnitHierarchy() {
  const { setRoot, rootStyle } = useScreenLock();

  const [context, setContext] = useState<ErpContext | null>(null);
  const [units, setUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [search, setSearch] = useState('');

  /*
   * THE RIGHT-SIDE PANEL, not a dialog — D-017. `open` is held separately from `unit` so the panel
   * can close on the record it was showing rather than on `null`.
   */
  const [panel, setPanel] = useState<{ open: boolean; unit: BusinessUnit | null }>({
    open: false,
    unit: null,
  });
  const [confirmDelete, setConfirmDelete] = useState<BusinessUnit | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [contextPayload, listPayload] = await Promise.all([
        erpApi.context(),
        erpApi.businessUnits.list(),
      ]);
      setContext(contextPayload.context);
      setUnits(listPayload.business_units);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load business units');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canEdit = canAdministerInitiatives(context);
  const filtering = search.trim() !== '';
  /* The server already returns the list ordered `name ASC, id ASC` (§5.1), so the rows are the
     filtered list in the order it arrived — no second sort, and no second answer to "what order is
     this list in". */
  const rows = useMemo(() => filterUnits(units, search), [units, search]);

  /** The panel has written. The notice, the close and the reload are the screen's. */
  const onPanelSaved = (message: string) => {
    setNotice(message);
    setPanel((prev) => ({ ...prev, open: false }));
    void load();
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    setActionError(null);
    try {
      await erpApi.businessUnits.remove(confirmDelete.id);
      setNotice('Business unit deleted.');
      setConfirmDelete(null);
      await load();
    } catch (err) {
      /*
       * The server's guard — a unit an initiative is still assessing — comes back with the wording
       * the server chose, and it is shown VERBATIM. Paraphrasing it here would mean two
       * descriptions of one rule.
       */
      setConfirmDelete(null);
      setActionError(err instanceof Error ? err.message : 'Could not delete this business unit');
    } finally {
      setBusy(false);
    }
  };

  const addButton = (
    <button
      type="button"
      className="erp-rl-btn erp-rl-btn--primary"
      onClick={() => setPanel({ open: true, unit: null })}
    >
      <i className="material-icons">add</i>
      Add Business Unit
    </button>
  );

  return (
    <ErpScreen>
      <div className="page bu-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={BusinessUnitsHelp} />
        <PageHeader
          title="Business Units"
          subtitle="The organisation this module assesses across — install-wide, and grouped by region and country"
          actions={canEdit ? addButton : null}
        />

        {/* The filter bar — the pattern's, `../chrome/recordList.css`. Part of the locked region, so
            it never scrolls. The status select is gone with the `active` column (D-022); the search
            stays, because it is the pattern's and because it is answerable from the list the screen
            already holds. */}
        <div className="erp-rl-filter-bar">
          <div className="erp-rl-search">
            <i className="material-icons">search</i>
            <input
              type="text"
              className="erp-rl-input"
              placeholder="Search units, regions, countries…"
              aria-label="Search business units"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {filtering && (
            <button
              type="button"
              className="erp-rl-btn erp-rl-btn--ghost"
              onClick={() => setSearch('')}
            >
              <i className="material-icons">clear</i>
              Clear
            </button>
          )}
        </div>

        {/* The screen's own messages, above the data and inside the locked region. A save refusal
            shows inside the panel instead; this is for the delete guard, which happens with no
            panel open. */}
        {actionError !== null && (
          <div className="erp-panel-error" style={{ flexShrink: 0 }}>
            <i className="material-icons erp-rl-error-icon">error</i>
            {actionError}
          </div>
        )}
        {notice !== null && (
          <div className="bu-results">
            <span>{notice}</span>
            <button
              type="button"
              className="erp-rl-btn erp-rl-btn--ghost"
              onClick={() => setNotice(null)}
            >
              <i className="material-icons">clear</i>
              Dismiss
            </button>
          </div>
        )}

        {!loading && loadError === null && units.length > 0 && (
          <div className="bu-results">
            <span>
              Showing {rows.length} of {units.length} business unit{units.length === 1 ? '' : 's'}
            </span>
          </div>
        )}

        {/* ── The three states, then the table. The pattern's icon-led versions. ───────────── */}
        {loading ? (
          <div className="erp-rl-loading">
            <i className="material-icons erp-rl-spin">sync</i>
            <p>Loading business units…</p>
          </div>
        ) : loadError !== null ? (
          <div className="erp-panel-error">
            <i className="material-icons erp-rl-error-icon">error</i>
            {loadError}
          </div>
        ) : rows.length === 0 ? (
          <div className="erp-rl-empty">
            <div className="erp-rl-empty-icon">
              <i className="material-icons">account_tree</i>
            </div>
            {units.length === 0 ? (
              <>
                <h2 className="erp-rl-empty-title">No business units yet</h2>
                <p className="erp-rl-empty-body">
                  Add the units you will actually assess — the divisions, plants or regions that own
                  systems.
                </p>
                {canEdit && <div style={{ marginTop: 12 }}>{addButton}</div>}
              </>
            ) : (
              <>
                <h2 className="erp-rl-empty-title">No matching business units</h2>
                <p className="erp-rl-empty-body">Try adjusting your search.</p>
              </>
            )}
          </div>
        ) : (
          /* The ONE scroller on this screen. Its column headers are sticky — see
             `../chrome/recordList.css`, which uses `border-collapse: separate` precisely so they
             work. */
          <div className="erp-rl-table-wrapper">
            <table className="erp-rl-table">
              <thead>
                <tr>
                  <th>Business unit</th>
                  <th>Region</th>
                  <th>Country</th>
                  <th className="erp-rl-right">Units</th>
                  <th className="erp-rl-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {/* The pattern's name-over-description primary cell
                          (`KnowledgeLibrary.tsx:941-950`). No indent and no toggle: the list is
                          flat (D-022). */}
                      <span className="erp-rl-name">{row.name}</span>
                      {row.description && <span className="erp-rl-desc">{row.description}</span>}
                    </td>
                    <td>
                      {/* The one classifying value on this record — the dimension you would group
                          the organisation by — so it is the pattern's badge. §1.9.2's measured info
                          pair, 7.04:1. */}
                      {row.region ? (
                        <span className="erp-rl-badge erp-rl-badge--info">{row.region}</span>
                      ) : (
                        <span className="erp-rl-none">N/A</span>
                      )}
                    </td>
                    <td className="erp-rl-sec">{row.country ?? 'N/A'}</td>
                    <td className="erp-rl-num">{row.assessment_unit_count}</td>
                    <td>
                      {canEdit ? (
                        <div className="erp-rl-actions">
                          {/* ✏️ then 🗑️ — the platform's established row-icon order, and the same
                              outlined glyph buttons the initiatives list and the Knowledge Library
                              use. The eye is absent because this table shows every field the record
                              has; there is nothing a view-only panel would add. */}
                          <button
                            type="button"
                            className="erp-rl-glyph"
                            title="Edit"
                            aria-label={`Edit ${row.name}`}
                            onClick={() => setPanel({ open: true, unit: row })}
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            className="erp-rl-glyph erp-rl-glyph--delete"
                            title="Delete"
                            aria-label={`Delete ${row.name}`}
                            onClick={() => setConfirmDelete(row)}
                          >
                            🗑️
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <BusinessUnitDetailPanel
          open={panel.open}
          unit={panel.unit}
          onClose={() => setPanel((prev) => ({ ...prev, open: false }))}
          onSaved={onPanelSaved}
        />

        {/* A CONFIRMATION, so it stays a dialog — D-017: no fields, a yes-or-no question about an
            action already chosen, and it must interrupt. `ConfirmDialog` is the platform's own
            ui-kit component, imported rather than cloned. */}
        <ConfirmDialog
          open={confirmDelete !== null}
          title="Delete business unit"
          message={
            confirmDelete
              ? `Delete ${confirmDelete.name}? A unit any initiative is assessing cannot be deleted — the server will say so.`
              : ''
          }
          confirmLabel="Delete"
          destructive
          onCancel={() => setConfirmDelete(null)}
          busy={busy}
          onConfirm={() => void remove()}
        />
      </div>
    </ErpScreen>
  );
}
